import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL, getImageUrl } from '../../config';
import { toastError, toastSuccess, errorMessage } from '../../lib/toast';

const TOOL_TYPE_LABEL = { tool: 'Tool', service: 'Service' };

// Mandatory, server-injected tool shown on every app request as a pinned,
// locked card. The client never sends it — the server appends it on submit —
// but it's displayed so the requester sees it's always included.
const SSO_TOOL_NAME = 'Google SSO';
const SSO_TOOL_ICON = '/images/google-sso.png';
const SSO_TOOL_NOTE =
  'Single sign-on with Google is required for every internal app. It authenticates ' +
  'users against the company Google Workspace so only authorized employees can sign ' +
  'in, removes the need to build or store a separate password system, and gives a ' +
  'consistent, audited login across all internal apps.';

// Combobox-style multiselect: type to search, results appear in a popover
// anchored under the input, and picking one adds a chip in-place (the input
// stays focused so several teammates can be added in a row). Selected chips
// render above the input; the result popover only shows while there's a query
// and the input has focus.
export function CollaboratorPicker({
  collaborators,
  onAdd,
  onRemove,
  excludeIds = null,
  searchUrl = '/api/app_collaborators/search',
  hint = "Add teammates who’ll work on this app. They’ll see it in their app requests.",
  placeholder = 'Search teammates by name or email…',
  noMatchLabel = 'No teammates match',
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);

  const mountedRef = useRef(true);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  useEffect(() => {
    let active = true;
    const q = query.trim();
    if (!q) { setResults([]); setSearching(false); return undefined; }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API_BASE_URL}${searchUrl}`, {
          params: { q }, withCredentials: true,
        });
        if (active && mountedRef.current) setResults(Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.users) ? res.data.users : []));
      } catch {
        if (active && mountedRef.current) setResults([]);
      } finally {
        if (active && mountedRef.current) setSearching(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(handle); };
  }, [query, searchUrl]);

  // Close the popover on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const selectedIds = useMemo(() => new Set(collaborators.map((c) => c.id)), [collaborators]);
  // Ids that must never appear as collaborators (e.g. the app's owner/creator).
  const excluded = useMemo(() => new Set(excludeIds || []), [excludeIds]);
  const visibleResults = useMemo(
    () => results.filter((u) => !selectedIds.has(u.id) && !excluded.has(u.id)),
    [results, selectedIds, excluded]
  );

  const pick = (u) => {
    onAdd(u);
    setQuery('');
    setResults([]);
    if (inputRef.current) inputRef.current.focus();
  };

  const showPopover = open && query.trim().length > 0;

  return (
    <div className="ba-collab" ref={wrapRef}>
      <p className="ba-collab-hint">{hint}</p>

      {collaborators.length > 0 && (
        <ul className="ba-chip-row" aria-label="Selected collaborators">
          {collaborators.map((c) => (
            <li key={c.id} className="ba-chip">
              {c.photo_url
                ? <img className="ba-chip-avatar" src={getImageUrl(c.photo_url)} alt="" />
                : <span className="ba-chip-avatar ba-chip-avatar--fallback" aria-hidden="true">{(c.name || '?').charAt(0).toUpperCase()}</span>}
              <span className="ba-chip-label">{c.name}</span>
              <button
                type="button"
                className="ba-chip-remove"
                aria-label={`Remove ${c.name}`}
                onClick={() => onRemove(c.id)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="ba-collab-combo">
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          placeholder={placeholder}
          value={query}
          role="combobox"
          aria-expanded={showPopover}
          aria-controls="ba-collab-listbox"
          autoComplete="off"
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter') { e.preventDefault(); if (visibleResults[0]) pick(visibleResults[0]); }
          }}
        />

        {showPopover && (
          <div className="ba-collab-popover" id="ba-collab-listbox" role="listbox">
            {searching && <p className="ba-collab-loading">Searching…</p>}
            {!searching && visibleResults.length === 0 && (
              <p className="ba-collab-empty">{noMatchLabel} "{query.trim()}".</p>
            )}
            {!searching && visibleResults.map((u) => (
              <button
                key={u.id}
                type="button"
                role="option"
                aria-selected="false"
                className="ba-collab-option"
                onClick={() => pick(u)}
              >
                {u.photo_url
                  ? <img className="ba-chip-avatar" src={getImageUrl(u.photo_url)} alt="" />
                  : <span className="ba-chip-avatar ba-chip-avatar--fallback" aria-hidden="true">{(u.name || '?').charAt(0).toUpperCase()}</span>}
                <span className="ba-collab-result-text">
                  <span className="ba-collab-result-name">{u.name}</span>
                  <span className="ba-collab-result-email">{u.email}</span>
                </span>
                <span className="ba-collab-add" aria-hidden="true">+</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Build initial form state from an app's `/detail` payload (edit mode).
export function seedFromInitial(initial) {
  const tools = Array.isArray(initial?.tools) ? initial.tools : [];
  const selected = {};
  const customTools = [];
  for (const t of tools) {
    // Skip the server-injected SSO row — it's pinned/locked, not a user-editable
    // custom tool, and the server re-injects it on save.
    if (t.is_custom && (t.tool_name || '').toLowerCase() === SSO_TOOL_NAME.toLowerCase()) continue;
    if (t.is_custom) customTools.push({ name: t.tool_name, usage_note: t.usage_note || '' });
    else if (t.tool_id != null) selected[t.tool_id] = t.usage_note || '';
  }
  const collaborators = (initial?.collaborators || []).map((c) => ({
    id: c.user_id, name: c.name, email: c.email, photo_url: c.photo_url,
  }));
  return {
    name: initial?.name || '',
    description: initial?.description || '',
    selected,
    customTools,
    collaborators,
  };
}

function AppRequestForm({ catalog, onSubmitted, initial = null, editId = null, onSubmit = null, headerSlot = null, excludeCollaboratorIds = null, collaboratorSearchUrl = '/api/app_collaborators/search' }) {
  const isEdit = editId != null;
  const seed = useMemo(() => seedFromInitial(initial), [initial]);
  const [name, setName] = useState(seed.name);
  const [description, setDescription] = useState(seed.description);
  const [selected, setSelected] = useState(seed.selected); // tool_id -> usage_note
  const [customTools, setCustomTools] = useState(seed.customTools); // [{ name, usage_note }]
  const [customName, setCustomName] = useState('');
  const [customNote, setCustomNote] = useState('');
  const [collaborators, setCollaborators] = useState(seed.collaborators); // [{id,name,email,photo_url}]
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const addCollaborator = (user) => {
    setCollaborators((prev) => (prev.some((c) => c.id === user.id) ? prev : [...prev, user]));
  };
  const removeCollaborator = (id) => {
    setCollaborators((prev) => prev.filter((c) => c.id !== id));
  };

  const addCustomTool = () => {
    const name = customName.trim();
    const note = customNote.trim();
    if (!name || !note) {
      setError('Add both a name and a usage note for the custom tool.');
      return;
    }
    setError(null);
    setCustomTools((prev) => [...prev, { name, usage_note: note }]);
    setCustomName('');
    setCustomNote('');
  };
  const removeCustomTool = (index) => {
    setCustomTools((prev) => prev.filter((_, i) => i !== index));
  };

  const filteredTools = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((t) =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    );
  }, [catalog, searchQuery]);

  const toggleTool = (id) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (id in next) delete next[id];
      else next[id] = '';
      return next;
    });
  };

  const setNote = (id, note) => setSelected((prev) => ({ ...prev, [id]: note }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const tools = Object.entries(selected).map(([toolId, usageNote]) => ({
      tool_id: Number(toolId), usage_note: usageNote.trim(),
    }));
    if (!name.trim() || !description.trim()) { setError('Name and description are required.'); return; }
    if (tools.length === 0 && customTools.length === 0) {
      setError('Select at least one tool, or add a custom one.'); return;
    }
    if (tools.some((t) => !t.usage_note)) { setError('Add a usage note for each selected tool.'); return; }
    const payload = {
      name: name.trim(),
      description: description.trim(),
      tools,
      custom_tools: customTools.map((c) => ({ name: c.name, usage_note: c.usage_note })),
      collaborators: collaborators.map((c) => c.id),
    };
    setSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit(payload);
      } else if (isEdit) {
        await axios.put(`${API_BASE_URL}/api/app_requests/${editId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_BASE_URL}/api/app_requests`, payload, { withCredentials: true });
      }
      if (mountedRef.current) {
        setSubmitting(false);
        setName(''); setDescription(''); setSelected({}); setSearchQuery('');
        setCustomTools([]); setCustomName(''); setCustomNote('');
        setCollaborators([]);
        if (!onSubmit) toastSuccess(isEdit ? 'Changes saved.' : 'App request submitted.');
        await onSubmitted();
      }
    } catch (err) {
      if (mountedRef.current) {
        if (!onSubmit) toastError(errorMessage(err, isEdit ? "Couldn’t save changes. Try again." : "Couldn’t submit your request. Try again."));
        setSubmitting(false);
        if (onSubmit) throw err; // only re-throw when the caller owns error handling
      }
    }
  };

  return (
    <form onSubmit={submit} className="ba-form">
      {headerSlot}
      <div className="ba-picker">
        <div className="ba-picker-head">
          <span className="ba-picker-label">The basics</span>
        </div>
        <div className="form-group">
          <label htmlFor="ba-name">App name</label>
          <input
            id="ba-name"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sales Insights Dashboard"
          />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="ba-desc">What does it do?</label>
          <textarea
            id="ba-desc"
            value={description}
            maxLength={2000}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
      </div>

      <div className="ba-picker">
        <div className="ba-picker-head">
          <span className="ba-picker-label">Team / collaborators (optional)</span>
        </div>
        <CollaboratorPicker
          collaborators={collaborators}
          onAdd={addCollaborator}
          onRemove={removeCollaborator}
          excludeIds={excludeCollaboratorIds}
          searchUrl={collaboratorSearchUrl}
        />
      </div>

      <div className="ba-picker">
        <div className="ba-picker-head">
          <span className="ba-picker-label">Always included</span>
          <span className="ba-picker-hint">Required on every app — you don&apos;t need to add this.</span>
        </div>
        <div className="ba-sso-card">
          <span className="ba-sso-icon" aria-hidden="true">
            <img src={getImageUrl(SSO_TOOL_ICON)} alt="" width="26" height="26" />
          </span>
          <div className="ba-sso-body">
            <div className="ba-sso-head">
              <strong className="ba-sso-name">{SSO_TOOL_NAME}</strong>
              <span className="ba-sso-lock" title="Required — can't be removed">Required</span>
            </div>
            <p className="ba-sso-note">{SSO_TOOL_NOTE}</p>
          </div>
        </div>
      </div>

      <div className="ba-picker">
        <div className="ba-picker-head">
          <span className="ba-picker-label">Pick the tools your app needs, and how it&apos;ll use each one&apos;s data</span>
          <input
            type="text"
            placeholder="Search your tools by name or description…"
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {catalog.length === 0 ? (
          <p className="ba-empty">
            You don&apos;t have access to any tools or services yet. Request access first, then build an app.
          </p>
        ) : filteredTools.length === 0 ? (
          <p className="ba-empty">No tools match your search.</p>
        ) : (
          <div className="tool-grid">
            {filteredTools.map((tool) => {
              const checked = tool.id in selected;
              return (
                <div key={tool.id} className={`tool-card ba-tool-card ${checked ? 'is-selected' : ''}`}>
                  <button
                    type="button"
                    className="ba-tool-select"
                    aria-pressed={checked}
                    onClick={() => toggleTool(tool.id)}
                  >
                    <div className="tool-card-header">
                      <div className="tool-card-icon">
                        {tool.icon_url && (
                          <img
                            src={getImageUrl(tool.icon_url)}
                            alt={tool.name}
                            style={{ width: 28, height: 28, objectFit: 'contain' }}
                          />
                        )}
                      </div>
                      <div className="tool-card-info">
                        <h4 className="tool-card-title">{tool.name}</h4>
                        <div className="tool-card-meta">
                          <span className="tool-card-category">{tool.category || 'Uncategorized'}</span>
                          <span className={`tool-type-badge tool-type-badge--${tool.type || 'tool'}`}>
                            {TOOL_TYPE_LABEL[tool.type] || 'Tool'}
                          </span>
                        </div>
                      </div>
                      <span className={`ba-check ${checked ? 'is-selected' : ''}`} aria-hidden="true">
                        {checked ? '✓' : '+'}
                      </span>
                    </div>
                    {tool.description && <p className="tool-card-desc">{tool.description}</p>}
                  </button>

                  {checked && (
                    <textarea
                      className="ba-tool-note"
                      aria-label={`How will you use ${tool.name}'s data?`}
                      placeholder="How do you want to use this tool's data?"
                      maxLength={1000}
                      value={selected[tool.id]}
                      onChange={(e) => setNote(tool.id, e.target.value)}
                      rows={2}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="ba-picker">
        <div className="ba-picker-head">
          <span className="ba-picker-label">Can&apos;t find a tool? Add it manually</span>
          <span className="ba-picker-hint">
            For a tool or dataset that isn&apos;t in your catalog yet.
          </span>
        </div>

        {customTools.length > 0 && (
          <ul className="ba-chip-row" aria-label="Custom tools">
            {customTools.map((c, i) => (
              <li key={`${c.name}-${i}`} className="ba-chip ba-chip--custom">
                <span className="ba-chip-label">
                  <strong>{c.name}</strong>: {c.usage_note}
                </span>
                <button
                  type="button"
                  className="ba-chip-remove"
                  aria-label={`Remove ${c.name}`}
                  onClick={() => removeCustomTool(i)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="form-group">
          <input
            type="text"
            className="search-input"
            placeholder="Tool or dataset name"
            maxLength={200}
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            aria-label="Custom tool name"
          />
        </div>
        <div className="form-group">
          <textarea
            className="ba-tool-note"
            placeholder="How will your app use this tool's data?"
            maxLength={1000}
            rows={2}
            value={customNote}
            onChange={(e) => setCustomNote(e.target.value)}
            aria-label="Custom tool usage note"
          />
        </div>
        <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
          <button
            type="button"
            className="btn btn-secondary btn-small"
            onClick={addCustomTool}
            disabled={!customName.trim() || !customNote.trim()}
          >
            Add custom tool
          </button>
        </div>
      </div>

      <div className="form-actions ba-submit-row">
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting
            ? (isEdit ? 'Saving…' : 'Submitting…')
            : (isEdit ? 'Save changes' : 'Submit request')}
        </button>
        {error && <p className="ba-error" role="alert">{error}</p>}
      </div>
    </form>
  );
}

export default AppRequestForm;
